import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useSeoKeywordsEditor } from '@/contexts/SeoKeywordsEditorContext';
import { useBrandManager } from '@/hooks/useBrandManager';
import { supabase } from '@/integrations/supabase/client';
import { Drawer, DrawerContent } from '@/components/ui/drawer';
import { useSidebar } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  TrendingUp,
  TrendingDown,
  Minus,
  Search,
  Eye,
  MousePointer,
  Target,
  X,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

interface KeywordPageRow {
  keyword: string;
  page: string;
  clicks: number;
  impressions: number;
  ctr: number;
  position: number;
}

interface QueryGroup {
  query: string;
  totalClicks: number;
  totalImpressions: number;
  avgCtr: number;
  avgPosition: number;
  pageCount: number;
  pages: Array<{
    page: string;
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  }>;
  changePercent?: number;
  positionChange?: number;
}

type SortField = "query" | "totalClicks" | "totalImpressions" | "avgCtr" | "avgPosition" | "pageCount" | "changePercent";
type SortDirection = "asc" | "desc";

export function GlobalSeoKeywordsEditor() {
  const { isOpen, closeEditor } = useSeoKeywordsEditor();
  const { currentBrandId } = useBrandManager();
  const { toast } = useToast();
  const { state: sidebarState, isMobile } = useSidebar();
  const location = useLocation();

  const [keywordPageData, setKeywordPageData] = useState<KeywordPageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState<SortField>("totalImpressions");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [expandedQueries, setExpandedQueries] = useState<Set<string>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [activeTab, setActiveTab] = useState("all");

  // Close drawer when route changes
  useEffect(() => {
    if (isOpen) {
      closeEditor();
    }
  }, [location.pathname]);

  // Calculate drawer dimensions
  const SIDEBAR_WIDTH = '16rem';
  const SIDEBAR_WIDTH_ICON = '3rem';
  const MOBILE_HEADER_HEIGHT = '57px';
  
  const drawerWidth = isMobile 
    ? '100%' 
    : sidebarState === 'expanded' 
      ? `calc(100% - ${SIDEBAR_WIDTH})` 
      : `calc(100% - ${SIDEBAR_WIDTH_ICON})`;
  
  const drawerLeft = isMobile 
    ? '0' 
    : sidebarState === 'expanded' 
      ? SIDEBAR_WIDTH 
      : SIDEBAR_WIDTH_ICON;

  useEffect(() => {
    if (currentBrandId && isOpen) {
      fetchKeywordData();
    }
  }, [currentBrandId, isOpen]);

  const fetchKeywordData = async () => {
    if (!currentBrandId) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("seo_3d_keyword_page_matching")
        .select("keyword, page, clicks, impressions, ctr, position")
        .eq("brand_id", currentBrandId);

      if (error) throw error;
      setKeywordPageData(data || []);
    } catch (error) {
      console.error("Error fetching keyword data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch keyword data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const groupedQueries = useMemo(() => {
    const queryMap = new Map<string, QueryGroup>();

    keywordPageData.forEach((row) => {
      const queryLower = row.keyword.toLowerCase();
      if (!queryMap.has(queryLower)) {
        queryMap.set(queryLower, {
          query: row.keyword,
          totalClicks: 0,
          totalImpressions: 0,
          avgCtr: 0,
          avgPosition: 0,
          pageCount: 0,
          pages: [],
        });
      }

      const group = queryMap.get(queryLower)!;
      group.totalClicks += row.clicks || 0;
      group.totalImpressions += row.impressions || 0;

      const existingPage = group.pages.find((p) => p.page === row.page);
      if (existingPage) {
        existingPage.clicks += row.clicks || 0;
        existingPage.impressions += row.impressions || 0;
        existingPage.position = (existingPage.position + row.position) / 2;
        existingPage.ctr = existingPage.impressions > 0 
          ? existingPage.clicks / existingPage.impressions 
          : 0;
      } else {
        group.pages.push({
          page: row.page,
          clicks: row.clicks || 0,
          impressions: row.impressions || 0,
          ctr: row.ctr || 0,
          position: row.position || 0,
        });
      }
    });

    queryMap.forEach((group) => {
      group.avgCtr = group.totalImpressions > 0 ? group.totalClicks / group.totalImpressions : 0;
      group.avgPosition =
        group.totalImpressions > 0
          ? group.pages.reduce((sum, p) => sum + p.position * p.impressions, 0) /
            group.totalImpressions
          : 0;
      group.pageCount = group.pages.length;
      group.pages.sort((a, b) => b.impressions - a.impressions);
    });

    return Array.from(queryMap.values());
  }, [keywordPageData]);

  const sortedQueries = useMemo(() => {
    const sorted = [...groupedQueries].sort((a, b) => {
      let aVal: number;
      let bVal: number;

      switch (sortField) {
        case "query":
          return sortDirection === "asc"
            ? a.query.localeCompare(b.query)
            : b.query.localeCompare(a.query);
        case "totalClicks":
          aVal = a.totalClicks;
          bVal = b.totalClicks;
          break;
        case "totalImpressions":
          aVal = a.totalImpressions;
          bVal = b.totalImpressions;
          break;
        case "avgCtr":
          aVal = a.avgCtr;
          bVal = b.avgCtr;
          break;
        case "avgPosition":
          aVal = a.avgPosition;
          bVal = b.avgPosition;
          break;
        case "pageCount":
          aVal = a.pageCount;
          bVal = b.pageCount;
          break;
        case "changePercent":
          aVal = a.changePercent || 0;
          bVal = b.changePercent || 0;
          break;
        default:
          return 0;
      }

      return sortDirection === "asc" ? aVal - bVal : bVal - aVal;
    });

    return sorted;
  }, [groupedQueries, sortField, sortDirection]);

  const getFilteredByTab = (tab: string): QueryGroup[] => {
    switch (tab) {
      case "new-opportunities":
        return sortedQueries.filter((q) => q.avgPosition <= 10);
      case "lost-opportunities":
        return sortedQueries.filter((q) => q.avgCtr < 0.02);
      case "low-hanging-fruit":
        return sortedQueries.filter(
          (q) => q.totalImpressions > 100 && q.avgCtr < 0.03 && q.avgPosition <= 50
        );
      case "top-3":
        return sortedQueries.filter((q) => q.avgPosition <= 3);
      case "top-10":
        return sortedQueries.filter((q) => q.avgPosition <= 10);
      case "outside-top-10":
        return sortedQueries.filter((q) => q.avgPosition > 10);
      default:
        return sortedQueries;
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection(field === "avgPosition" ? "asc" : "desc");
    }
  };

  const toggleQueryExpand = (query: string) => {
    const newExpanded = new Set(expandedQueries);
    if (newExpanded.has(query)) {
      newExpanded.delete(query);
    } else {
      newExpanded.add(query);
    }
    setExpandedQueries(newExpanded);
  };

  const paginatedQueries = (queries: QueryGroup[]) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return queries.slice(startIndex, endIndex);
  };

  const stats = useMemo(() => {
    return {
      totalQueries: groupedQueries.length,
      totalClicks: groupedQueries.reduce((sum, q) => sum + q.totalClicks, 0),
      avgCtr: groupedQueries.length > 0
        ? (groupedQueries.reduce((sum, q) => sum + q.avgCtr, 0) / groupedQueries.length) * 100
        : 0,
      avgPosition: groupedQueries.length > 0
        ? groupedQueries.reduce((sum, q) => sum + q.avgPosition, 0) / groupedQueries.length
        : 0,
    };
  }, [groupedQueries]);

  const SortButton = ({ field, children }: { field: SortField; children: React.ReactNode }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => handleSort(field)}
      className="h-8 gap-1 font-semibold"
    >
      {children}
      {sortField === field ? (
        sortDirection === "asc" ? (
          <ArrowUp className="h-3 w-3" />
        ) : (
          <ArrowDown className="h-3 w-3" />
        )
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-50" />
      )}
    </Button>
  );

  const ChangeIndicator = ({ value, isPosition }: { value?: number; isPosition?: boolean }) => {
    if (value === undefined) return <Minus className="h-4 w-4 text-muted-foreground" />;

    const isPositive = isPosition ? value > 0 : value > 0;
    const color = isPositive ? "text-green-600" : value < 0 ? "text-red-600" : "text-muted-foreground";

    return (
      <div className={`flex items-center gap-1 ${color}`}>
        {isPositive ? (
          <TrendingUp className="h-4 w-4" />
        ) : value < 0 ? (
          <TrendingDown className="h-4 w-4" />
        ) : (
          <Minus className="h-4 w-4" />
        )}
        <span className="text-sm font-medium">
          {isPosition
            ? `${value > 0 ? "+" : ""}${value.toFixed(1)}`
            : `${value > 0 ? "+" : ""}${value.toFixed(1)}%`}
        </span>
      </div>
    );
  };

  // Mobile Card View
  const MobileQueryCard = ({ query }: { query: QueryGroup }) => {
    const isExpanded = expandedQueries.has(query.query);
    
    return (
      <Card className="mb-2">
        <Collapsible open={isExpanded} onOpenChange={() => toggleQueryExpand(query.query)}>
          <CollapsibleTrigger asChild>
            <CardContent className="p-3 cursor-pointer">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{query.query}</p>
                  {query.pageCount > 1 && (
                    <Badge variant="outline" className="text-xs mt-1">
                      {query.pageCount} pages
                    </Badge>
                  )}
                </div>
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
              </div>
              <div className="grid grid-cols-4 gap-2 mt-2 text-xs">
                <div>
                  <p className="text-muted-foreground">Clicks</p>
                  <p className="font-medium">{query.totalClicks.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Impr</p>
                  <p className="font-medium">{query.totalImpressions.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">CTR</p>
                  <p className="font-medium">{(query.avgCtr * 100).toFixed(2)}%</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pos</p>
                  <p className="font-medium">{query.avgPosition.toFixed(1)}</p>
                </div>
              </div>
            </CardContent>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="px-3 pb-3 space-y-2">
              <p className="text-xs font-semibold text-muted-foreground">Ranking Pages:</p>
              {query.pages.map((page, idx) => (
                <div
                  key={idx}
                  className="text-xs border-l-2 border-primary pl-2 py-1"
                >
                  <p className="truncate text-muted-foreground">{page.page}</p>
                  <div className="flex gap-3 mt-1">
                    <span>Pos: {page.position.toFixed(1)}</span>
                    <span>Clicks: {page.clicks}</span>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      </Card>
    );
  };

  const filteredQueries = getFilteredByTab(activeTab);

  return (
    <Drawer 
      open={isOpen} 
      onOpenChange={(open) => !open && closeEditor()}
      direction="right"
      modal={false}
    >
      <DrawerContent
        className="fixed inset-y-0 right-0 h-full border-l bg-background shadow-lg focus:outline-none mt-0"
        style={{ 
          width: drawerWidth,
          left: drawerLeft,
          top: isMobile ? MOBILE_HEADER_HEIGHT : 0,
          height: isMobile ? `calc(100% - ${MOBILE_HEADER_HEIGHT})` : '100%',
        }}
      >
        <ScrollArea className="h-full">
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-2">
              {/* Row 1: Title + Close */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl sm:text-2xl font-bold">Query Analysis</h2>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeEditor}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              {/* Row 2: Subtitle */}
              <p className="text-sm text-muted-foreground">
                Analyze search queries and discover optimization opportunities
              </p>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                  <CardDescription className="text-xs sm:text-sm">Total Queries</CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="flex items-center gap-2">
                    <Search className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground hidden sm:block" />
                    <p className="text-lg sm:text-2xl font-bold">{stats.totalQueries.toLocaleString()}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                  <CardDescription className="text-xs sm:text-sm">Total Clicks</CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="flex items-center gap-2">
                    <MousePointer className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground hidden sm:block" />
                    <p className="text-lg sm:text-2xl font-bold">{stats.totalClicks.toLocaleString()}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                  <CardDescription className="text-xs sm:text-sm">Avg CTR</CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="flex items-center gap-2">
                    <Target className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground hidden sm:block" />
                    <p className="text-lg sm:text-2xl font-bold">{stats.avgCtr.toFixed(2)}%</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-1 sm:pb-2 p-3 sm:p-4">
                  <CardDescription className="text-xs sm:text-sm">Avg Position</CardDescription>
                </CardHeader>
                <CardContent className="p-3 sm:p-4 pt-0">
                  <div className="flex items-center gap-2">
                    <Eye className="h-3 w-3 sm:h-4 sm:w-4 text-muted-foreground hidden sm:block" />
                    <p className="text-lg sm:text-2xl font-bold">{stats.avgPosition.toFixed(1)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-96 w-full" />
              </div>
            ) : (
              <>
                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setCurrentPage(1); }} className="w-full">
                  {/* Mobile: Scrollable horizontal tabs */}
                  <div className="overflow-x-auto -mx-4 sm:mx-0 px-4 sm:px-0">
                    <TabsList className="inline-flex w-auto sm:grid sm:w-full sm:grid-cols-7 min-w-max">
                      <TabsTrigger value="all" className="text-xs sm:text-sm">All</TabsTrigger>
                      <TabsTrigger value="new-opportunities" className="text-xs sm:text-sm">New Opp.</TabsTrigger>
                      <TabsTrigger value="lost-opportunities" className="text-xs sm:text-sm">Lost Opp.</TabsTrigger>
                      <TabsTrigger value="low-hanging-fruit" className="text-xs sm:text-sm">Low Fruit</TabsTrigger>
                      <TabsTrigger value="top-3" className="text-xs sm:text-sm">Top 3</TabsTrigger>
                      <TabsTrigger value="top-10" className="text-xs sm:text-sm">Top 10</TabsTrigger>
                      <TabsTrigger value="outside-top-10" className="text-xs sm:text-sm">Outside 10</TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value={activeTab} className="mt-4">
                    {/* Mobile: Card View */}
                    <div className="block sm:hidden space-y-2">
                      {paginatedQueries(filteredQueries).length === 0 ? (
                        <p className="text-center py-8 text-muted-foreground">
                          No queries found for this filter
                        </p>
                      ) : (
                        paginatedQueries(filteredQueries).map((query) => (
                          <MobileQueryCard key={query.query} query={query} />
                        ))
                      )}
                    </div>

                    {/* Desktop: Table View */}
                    <Card className="hidden sm:block">
                      <CardContent className="p-0">
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[300px]">
                                  <SortButton field="query">Query</SortButton>
                                </TableHead>
                                <TableHead className="text-right">
                                  <SortButton field="totalClicks">Clicks</SortButton>
                                </TableHead>
                                <TableHead className="text-right">
                                  <SortButton field="totalImpressions">Impressions</SortButton>
                                </TableHead>
                                <TableHead className="text-right">
                                  <SortButton field="avgCtr">CTR</SortButton>
                                </TableHead>
                                <TableHead className="text-right">
                                  <SortButton field="avgPosition">Position</SortButton>
                                </TableHead>
                                <TableHead className="text-right">
                                  <SortButton field="changePercent">Change</SortButton>
                                </TableHead>
                                <TableHead className="text-right">
                                  <SortButton field="pageCount">Pages</SortButton>
                                </TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedQueries(filteredQueries).length === 0 ? (
                                <TableRow>
                                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                                    No queries found for this filter
                                  </TableCell>
                                </TableRow>
                              ) : (
                                paginatedQueries(filteredQueries).map((query) => (
                                  <Collapsible
                                    key={query.query}
                                    open={expandedQueries.has(query.query)}
                                    onOpenChange={() => toggleQueryExpand(query.query)}
                                    asChild
                                  >
                                    <>
                                      <TableRow className="cursor-pointer hover:bg-muted/50">
                                        <CollapsibleTrigger asChild>
                                          <TableCell className="font-medium">
                                            <div className="flex items-center gap-2">
                                              <span className="truncate max-w-[250px]">{query.query}</span>
                                              {query.pageCount > 1 && (
                                                <Badge variant="outline" className="text-xs">
                                                  {query.pageCount}
                                                </Badge>
                                              )}
                                            </div>
                                          </TableCell>
                                        </CollapsibleTrigger>
                                        <TableCell className="text-right">{query.totalClicks.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">{query.totalImpressions.toLocaleString()}</TableCell>
                                        <TableCell className="text-right">{(query.avgCtr * 100).toFixed(2)}%</TableCell>
                                        <TableCell className="text-right">{query.avgPosition.toFixed(1)}</TableCell>
                                        <TableCell className="text-right">
                                          <ChangeIndicator value={query.changePercent} />
                                        </TableCell>
                                        <TableCell className="text-right">{query.pageCount}</TableCell>
                                      </TableRow>
                                      {query.pageCount > 0 && (
                                        <CollapsibleContent asChild>
                                          <TableRow>
                                            <TableCell colSpan={7} className="bg-muted/30 p-4">
                                              <div className="space-y-2">
                                                <p className="text-sm font-semibold mb-2">
                                                  Ranking Pages ({query.pageCount}):
                                                </p>
                                                {query.pages.map((page, idx) => (
                                                  <div
                                                    key={idx}
                                                    className="flex items-center justify-between text-sm border-l-2 border-primary pl-3 py-1"
                                                  >
                                                    <span className="text-muted-foreground truncate max-w-[400px]">
                                                      {page.page}
                                                    </span>
                                                    <div className="flex items-center gap-4">
                                                      <span>Pos: {page.position.toFixed(1)}</span>
                                                      <span>Clicks: {page.clicks}</span>
                                                      <span>Impr: {page.impressions.toLocaleString()}</span>
                                                      <span>CTR: {(page.ctr * 100).toFixed(2)}%</span>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </TableCell>
                                          </TableRow>
                                        </CollapsibleContent>
                                      )}
                                    </>
                                  </Collapsible>
                                ))
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Pagination */}
                    {filteredQueries.length > 0 && (
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-4">
                        <div className="flex items-center gap-2 sm:gap-4">
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredQueries.length)} of {filteredQueries.length}
                          </p>
                          <Select
                            value={itemsPerPage.toString()}
                            onValueChange={(value) => {
                              setItemsPerPage(Number(value));
                              setCurrentPage(1);
                            }}
                          >
                            <SelectTrigger className="w-[80px] sm:w-[100px] h-8">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="25">25</SelectItem>
                              <SelectItem value="50">50</SelectItem>
                              <SelectItem value="100">100</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                          >
                            Prev
                          </Button>
                          <span className="text-xs sm:text-sm">
                            {currentPage} / {Math.ceil(filteredQueries.length / itemsPerPage)}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCurrentPage(Math.min(Math.ceil(filteredQueries.length / itemsPerPage), currentPage + 1))}
                            disabled={currentPage >= Math.ceil(filteredQueries.length / itemsPerPage)}
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
        </ScrollArea>
      </DrawerContent>
    </Drawer>
  );
}
